import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../api';
import {
  ErrorBox,
  Loader,
  PageHeader,
  formatDateTime,
  formatDuration,
  formatPrice,
} from '../components/Common';
import type { Master, Service, SlotCandidate } from '../types';

type Step = 'service' | 'master' | 'date' | 'time' | 'confirm';

export function BookWizardPage() {
  const params = useParams<{ serviceId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>(params.serviceId ? 'master' : 'service');
  const [serviceId, setServiceId] = useState<string | null>(params.serviceId ?? null);
  const [masterId, setMasterId] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<SlotCandidate | null>(null);
  const [note, setNote] = useState('');

  const services = useQuery({ queryKey: ['services'], queryFn: api.services.listPublic });
  const masters = useQuery({ queryKey: ['masters'], queryFn: api.masters.list });
  const dates = useQuery({
    queryKey: ['slots', 'dates', masterId, serviceId],
    queryFn: () =>
      api.slots.dates({
        master_id: masterId!,
        service_id: serviceId!,
        days_ahead: 21,
      }),
    enabled: !!masterId && !!serviceId && step === 'date',
  });
  const slots = useQuery({
    queryKey: ['slots', masterId, serviceId, date],
    queryFn: () =>
      api.slots.list({ master_id: masterId!, service_id: serviceId!, date: date! }),
    enabled: !!masterId && !!serviceId && !!date && step === 'time',
  });

  const selectedService: Service | undefined = useMemo(
    () => services.data?.find((s) => s.id === serviceId),
    [services.data, serviceId],
  );
  const selectedMaster: Master | undefined = useMemo(
    () => masters.data?.find((m) => m.id === masterId),
    [masters.data, masterId],
  );

  // Auto-advance when serviceId in URL exists
  useEffect(() => {
    if (params.serviceId && !serviceId) {
      setServiceId(params.serviceId);
      setStep('master');
    }
  }, [params.serviceId, serviceId]);

  const create = useMutation({
    mutationFn: () =>
      api.bookings.create({
        master_id: masterId!,
        service_id: serviceId!,
        slot_start: slot!.start,
        client_note: note || undefined,
        idempotency_key: crypto.randomUUID(),
      }),
    onSuccess: (b) => {
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      navigate(`/bookings/${b.id}`, { replace: true });
    },
  });

  return (
    <div>
      <PageHeader title="Запись" subtitle={subtitleFor(step)} />
      <Stepper step={step} />

      {step === 'service' && (
        <StepBody loading={services.isLoading} error={services.error}>
          <ul className="space-y-2">
            {services.data?.map((s) => (
              <li key={s.id}>
                <button
                  className="card flex w-full items-center justify-between text-left active:opacity-80"
                  onClick={() => {
                    setServiceId(s.id);
                    setStep('master');
                  }}
                >
                  <div>
                    <p className="font-semibold">{s.name}</p>
                    <p className="text-xs text-tg-hint">{formatDuration(s.duration_minutes)}</p>
                  </div>
                  <p className="font-semibold">{formatPrice(s.price_min, s.price_max)}</p>
                </button>
              </li>
            ))}
          </ul>
        </StepBody>
      )}

      {step === 'master' && (
        <StepBody loading={masters.isLoading} error={masters.error}>
          {selectedService && (
            <div className="card mb-3 text-sm text-tg-hint">
              Услуга: <b className="text-tg-text">{selectedService.name}</b>
            </div>
          )}
          <ul className="space-y-2">
            {masters.data?.map((m) => (
              <li key={m.id}>
                <button
                  className="card flex w-full items-center justify-between text-left active:opacity-80"
                  onClick={() => {
                    setMasterId(m.id);
                    setStep('date');
                  }}
                >
                  <div>
                    <p className="font-semibold">
                      {m.first_name} {m.last_name ?? ''}
                    </p>
                    {m.username && <p className="text-xs text-tg-hint">@{m.username}</p>}
                  </div>
                  <span>›</span>
                </button>
              </li>
            ))}
          </ul>
        </StepBody>
      )}

      {step === 'date' && (
        <StepBody loading={dates.isLoading} error={dates.error}>
          <p className="mb-2 text-sm text-tg-hint">
            Доступные даты у {selectedMaster?.first_name}
          </p>
          {dates.data && dates.data.length === 0 ? (
            <p className="text-sm text-tg-hint">Нет свободных дат на ближайшие 3 недели.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {dates.data?.map((d) => (
                <button
                  key={d}
                  className="card text-left active:opacity-80"
                  onClick={() => {
                    setDate(d);
                    setStep('time');
                  }}
                >
                  <p className="font-semibold">
                    {new Date(d).toLocaleDateString('ru-RU', {
                      day: '2-digit',
                      month: 'long',
                      weekday: 'short',
                    })}
                  </p>
                </button>
              ))}
            </div>
          )}
        </StepBody>
      )}

      {step === 'time' && (
        <StepBody loading={slots.isLoading} error={slots.error}>
          <p className="mb-2 text-sm text-tg-hint">
            Свободное время на{' '}
            {date &&
              new Date(date).toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: 'long',
              })}
          </p>
          {slots.data && slots.data.slots.length === 0 ? (
            <p className="text-sm text-tg-hint">Нет свободных слотов на этот день.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {slots.data?.slots.map((s) => (
                <button
                  key={s.start}
                  className="card text-center active:opacity-80"
                  onClick={() => {
                    setSlot(s);
                    setStep('confirm');
                  }}
                >
                  {new Date(s.start).toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </button>
              ))}
            </div>
          )}
        </StepBody>
      )}

      {step === 'confirm' && (
        <div className="space-y-3">
          <div className="card space-y-1">
            <Row label="Услуга" value={selectedService?.name} />
            <Row
              label="Длительность"
              value={selectedService ? formatDuration(selectedService.duration_minutes) : undefined}
            />
            <Row
              label="Стоимость"
              value={
                selectedService
                  ? formatPrice(selectedService.price_min, selectedService.price_max)
                  : undefined
              }
            />
            <Row
              label="Мастер"
              value={selectedMaster ? `${selectedMaster.first_name} ${selectedMaster.last_name ?? ''}`.trim() : undefined}
            />
            <Row label="Время" value={slot ? formatDateTime(slot.start) : undefined} />
          </div>

          <div>
            <label className="label">Комментарий мастеру (необязательно)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="input min-h-[80px]"
              placeholder="Например: длинные ногти, аллергия на..."
            />
          </div>

          {create.error && (
            <ErrorBox
              message={
                create.error instanceof ApiClientError
                  ? create.error.message
                  : (create.error as Error).message
              }
            />
          )}

          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setStep('time')}>
              Назад
            </button>
            <button
              className="btn-primary flex-1"
              disabled={create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Записываем...' : 'Записаться'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepBody({
  loading,
  error,
  children,
}: {
  loading?: boolean;
  error?: unknown;
  children: React.ReactNode;
}) {
  if (loading) return <Loader />;
  if (error) return <ErrorBox message={(error as Error).message} />;
  return <>{children}</>;
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-tg-hint">{label}</span>
      <span className="font-medium">{value ?? '—'}</span>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const all: Step[] = ['service', 'master', 'date', 'time', 'confirm'];
  const idx = all.indexOf(step);
  return (
    <div className="mb-4 flex items-center gap-1">
      {all.map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full ${i <= idx ? 'bg-tg-button' : 'bg-tg-hint/30'}`}
        />
      ))}
    </div>
  );
}

function subtitleFor(step: Step): string {
  return {
    service: 'Шаг 1 из 5: услуга',
    master: 'Шаг 2 из 5: мастер',
    date: 'Шаг 3 из 5: дата',
    time: 'Шаг 4 из 5: время',
    confirm: 'Шаг 5 из 5: подтверждение',
  }[step];
}
